import referralApi from '@/api/referral'
import {
  loadReditusScript, initReditusWidget, showReditusWidget, isMockMode, isMockAllowed,
} from '@/services/loaderPlugin/reditus.loader'
import Sentry from '@/services/sentry'
import utils from '@/util'
import i18nPresenter from '@/i18nPresenter'

export const WidgetFailure = {
  NO_GR: 'no_gr',
  API_MOCK: 'api_mock',
  FETCH_ERROR: 'fetch_error',
}

let pendingWidgetInit = null

const reportUnavailable = (failure) => {
  Sentry.captureMessage(`Referral widget unavailable: ${failure}`, 'warning')
}

const toastUnavailable = (failure) => {
  const message = failure === WidgetFailure.NO_GR
    ? 'Something in your browser (such as an ad blocker) is blocking the referral programme. Please allow ahaslides.com, then refresh the page.'
    : 'Referral program is temporarily unavailable. Please try again later.'
  utils.showToastError(i18nPresenter.t(message), 'referral-unavailable')
}

const state = {
  widgetLoaded: false,
  hasUnreadReward: false,
  mockMode: false,
  showMockModal: false,
  failure: '',
}

const _getters = {
  isWidgetReady: (s) => s.widgetLoaded,
  hasUnreadReward: (s) => s.hasUnreadReward,
  isMockMode: (s) => s.mockMode,
  showMockModal: (s) => s.showMockModal,
}

/* eslint-disable no-param-reassign */
const _mutations = {
  SET_WIDGET_LOADED(s, val) { s.widgetLoaded = val },
  SET_HAS_UNREAD_REWARD(s, val) { s.hasUnreadReward = val },
  SET_MOCK_MODE(s, val) { s.mockMode = val },
  SET_SHOW_MOCK_MODAL(s, val) { s.showMockModal = val },
  SET_FAILURE(s, val) { s.failure = val },
}
/* eslint-enable no-param-reassign */

const _actions = {
  async initWidget({ commit, rootState }) {
    try {
      referralApi.initResource()
      const tokenData = await referralApi.getToken()

      if (tokenData.mock) {
        if (!isMockAllowed()) {
          commit('SET_FAILURE', WidgetFailure.API_MOCK)
          commit('SET_WIDGET_LOADED', true)
          return
        }
        commit('SET_MOCK_MODE', true)
        commit('SET_WIDGET_LOADED', true)
        commit('SET_HAS_UNREAD_REWARD', true)
        return
      }

      await loadReditusScript()

      const user = rootState.currentUser
      pendingWidgetInit = [tokenData.productId, tokenData.token, {
        email: user.email,
        first_name: user.firstName,
        last_name: user.lastName,
      }]
      const initialised = initReditusWidget(...pendingWidgetInit)

      commit('SET_MOCK_MODE', isMockMode())
      if (initialised) {
        commit('SET_FAILURE', '')
      } else {
        commit('SET_FAILURE', WidgetFailure.NO_GR)
      }
      commit('SET_WIDGET_LOADED', true)
    } catch (err) {
      Sentry.captureException(err)
      if (!isMockAllowed()) {
        commit('SET_FAILURE', WidgetFailure.FETCH_ERROR)
        return
      }
      console.warn('Referral widget init failed, falling back to mock mode:', err.message)
      commit('SET_MOCK_MODE', true)
      commit('SET_WIDGET_LOADED', true)
    }
  },

  async fetchRewardStatus({ commit, state: s }) {
    if (s.mockMode) {
      commit('SET_HAS_UNREAD_REWARD', true)
      return
    }
    try {
      const data = await referralApi.getRewardStatus()
      commit('SET_HAS_UNREAD_REWARD', data.hasUnreadReward)
    } catch (err) {
      Sentry.captureException(err)
    }
  },

  async acknowledgeReward({ commit, state: s }) {
    commit('SET_HAS_UNREAD_REWARD', false)
    if (!s.mockMode) {
      try {
        await referralApi.acknowledgeReward()
      } catch (err) {
        Sentry.captureException(err)
      }
    }
  },

  openWidget({ commit, dispatch, state: s }) {
    if (s.mockMode) {
      commit('SET_SHOW_MOCK_MODAL', true)
    } else {
      if (s.failure === WidgetFailure.NO_GR && pendingWidgetInit && initReditusWidget(...pendingWidgetInit)) {
        commit('SET_FAILURE', '')
      }
      if (s.failure) {
        reportUnavailable(s.failure)
        toastUnavailable(s.failure)
        return
      }
      showReditusWidget()
    }
    if (s.hasUnreadReward) {
      dispatch('acknowledgeReward')
    }

    // Notify BE that the widget was opened so it can run its retry chain
    // to sync the advocate slug from Reditus. Fire-and-forget — the chain
    // runs entirely on the server and survives the user closing the app.
    if (!s.mockMode) {
      referralApi.notifyWidgetOpened().catch((err) => Sentry.captureException(err))
    }
  },

  closeMockModal({ commit }) {
    commit('SET_SHOW_MOCK_MODAL', false)
  },
}

export default {
  namespaced: true,
  state,
  getters: _getters,
  mutations: _mutations,
  actions: _actions,
}
